import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import translations from '@/locales/translations.json'

const SUPPORTED_LANGUAGES = ['de', 'en']
const DEFAULT_LANGUAGE = 'de'

function resolvePath(obj, path) {
  if (!obj || !path) return undefined
  return path.split('.').reduce((acc, part) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, part)) {
      return acc[part]
    }
    return undefined
  }, obj)
}

function detectInitialLanguage() {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }

  const stored = window.localStorage.getItem('language')
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored
  }

  const browserLang = window.navigator?.language?.slice(0, 2)?.toLowerCase()
  if (browserLang && SUPPORTED_LANGUAGES.includes(browserLang)) {
    return browserLang
  }

  return DEFAULT_LANGUAGE
}

export const useLanguageStore = defineStore('languageStore', () => {
  const currentLanguage = ref(detectInitialLanguage())

  function setLanguage(lang) {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return
    currentLanguage.value = lang
  }

  function toggleLanguage() {
    const currentIndex = SUPPORTED_LANGUAGES.indexOf(currentLanguage.value)
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length
    currentLanguage.value = SUPPORTED_LANGUAGES[nextIndex]
  }

  function t(key, replacements = {}) {
    const entry = resolvePath(translations, key)
    if (!entry) return key

    const value =
      entry[currentLanguage.value] ||
      entry[DEFAULT_LANGUAGE] ||
      entry.en ||
      key

    return Object.keys(replacements).reduce((acc, replacementKey) => {
      const placeholder = `{${replacementKey}}`
      return acc.split(placeholder).join(String(replacements[replacementKey]))
    }, value)
  }

  watch(
    currentLanguage,
    (lang) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('language', lang)
      }
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', lang)
      }
    },
    { immediate: true }
  )

  return {
    currentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    translations,
    setLanguage,
    toggleLanguage,
    t
  }
})

